<template>
  <div class="manager-profile-update">
    <div class="manager-profile-update-header">
      <h2 class="manager-profile-update-title">Update Profile</h2>
      <p class="manager-profile-update-subtitle">Manage your account information and preferences</p>
    </div>

    <div class="manager-profile-update-content">
      <div v-if="successMessage" class="manager-profile-update-alert manager-profile-update-alert-success">
        <i class="fas fa-check-circle"></i>
        {{ successMessage }}
      </div>

      <div v-if="error" class="manager-profile-update-alert manager-profile-update-alert-danger">
        <i class="fas fa-exclamation-circle"></i>
        {{ error }}
      </div>

      <form @submit.prevent="handleSubmit" class="manager-profile-update-form">
        <div class="manager-profile-update-section">
          <h3 class="manager-profile-update-section-title">Personal Information</h3>
          
          <div class="manager-profile-update-section-fields">
            <div class="manager-profile-update-form-group">
              <label for="firstName">First Name <span class="manager-profile-update-required">*</span></label>
              <input 
                id="firstName"
                type="text" 
                v-model="formData.firstName" 
                required 
                class="manager-profile-update-form-control"
                :class="{ 'manager-profile-update-form-control-error': errors.firstName }"
              />
              <span v-if="errors.firstName" class="manager-profile-update-error-text">{{ errors.firstName }}</span>
            </div>

            <div class="manager-profile-update-form-group">
              <label for="lastName">Last Name <span class="manager-profile-update-required">*</span></label>
              <input 
                id="lastName"
                type="text" 
                v-model="formData.lastName" 
                required 
                class="manager-profile-update-form-control"
                :class="{ 'manager-profile-update-form-control-error': errors.lastName }"
              />
              <span v-if="errors.lastName" class="manager-profile-update-error-text">{{ errors.lastName }}</span>
            </div>

            <div class="manager-profile-update-form-group">
              <label for="email">Email <span class="manager-profile-update-required">*</span></label>
              <input 
                id="email"
                type="email" 
                v-model="formData.email" 
                required 
                class="manager-profile-update-form-control"
                :class="{ 'manager-profile-update-form-control-error': errors.email }"
              />
              <span v-if="errors.email" class="manager-profile-update-error-text">{{ errors.email }}</span>
            </div>

            <div class="manager-profile-update-form-group">
              <label for="phone">Phone</label>
              <input 
                id="phone"
                type="tel" 
                v-model="formData.phone" 
                class="manager-profile-update-form-control"
                :class="{ 'manager-profile-update-form-control-error': errors.phone }"
                placeholder="+1 (555) 123-4567"
              />
              <span v-if="errors.phone" class="manager-profile-update-error-text">{{ errors.phone }}</span>
            </div>
          </div>
        </div>

        <div class="manager-profile-update-section">
          <h3 class="manager-profile-update-section-title">Change Password</h3>
          <p class="manager-profile-update-section-note">Leave blank if you don't want to change your password</p>
          
          <div class="manager-profile-update-section-fields">
            <div class="manager-profile-update-form-group">
              <label for="currentPassword">Current Password</label>
              <input 
                id="currentPassword"
                type="password" 
                v-model="formData.currentPassword" 
                class="manager-profile-update-form-control"
                :class="{ 'manager-profile-update-form-control-error': errors.currentPassword }"
                autocomplete="current-password"
              />
              <span v-if="errors.currentPassword" class="manager-profile-update-error-text">{{ errors.currentPassword }}</span>
            </div>

            <div class="manager-profile-update-form-group">
              <label for="newPassword">New Password</label>
              <input 
                id="newPassword"
                type="password" 
                v-model="formData.newPassword" 
                class="manager-profile-update-form-control"
                :class="{ 'manager-profile-update-form-control-error': errors.newPassword }"
                autocomplete="new-password"
                minlength="6"
              />
              <span v-if="errors.newPassword" class="manager-profile-update-error-text">{{ errors.newPassword }}</span>
              <small class="manager-profile-update-help-text">Minimum 6 characters</small>
            </div>

            <div class="manager-profile-update-form-group">
              <label for="confirmPassword">Confirm New Password</label>
              <input 
                id="confirmPassword"
                type="password" 
                v-model="formData.confirmPassword" 
                class="manager-profile-update-form-control"
                :class="{ 'manager-profile-update-form-control-error': errors.confirmPassword }"
                autocomplete="new-password"
              />
              <span v-if="errors.confirmPassword" class="manager-profile-update-error-text">{{ errors.confirmPassword }}</span>
            </div>
          </div>
        </div>

        <div class="manager-profile-update-actions">
          <button 
            type="button" 
            class="manager-profile-update-btn manager-profile-update-btn-secondary" 
            @click="resetForm"
            :disabled="loading"
          >
            Reset
          </button>
          <button 
            type="submit" 
            class="manager-profile-update-btn manager-profile-update-btn-primary" 
            :disabled="loading"
          >
            <span v-if="loading" class="manager-profile-update-spinner"></span>
            {{ loading ? 'Updating...' : 'Update Profile' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import '../styles/ManagerProfileUpdate.css';
import axios from 'axios';

export default {
  name: 'ManagerProfileUpdate',
  props: {
    managerUser: {
      type: Object,
      required: true
    }
  },
  emits: ['updated'],
  data() {
    return {
      formData: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      },
      errors: {},
      loading: false,
      error: '',
      successMessage: ''
    }
  },
  watch: {
    managerUser: {
      immediate: true,
      handler(newUser) {
        if (newUser) {
          this.loadUserData();
        }
      }
    }
  },
  methods: {
    loadUserData() {
      if (this.managerUser) {
        this.formData = {
          firstName: this.managerUser.firstName || '',
          lastName: this.managerUser.lastName || '',
          email: this.managerUser.email || '',
          phone: this.managerUser.phone || '',
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        };
      }
    },
    validateForm() {
      this.errors = {};
      let isValid = true;

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (this.formData.email && !emailRegex.test(this.formData.email)) {
        this.errors.email = 'Please enter a valid email address';
        isValid = false;
      }

      // Validate password if new password is provided
      if (this.formData.newPassword) {
        if (this.formData.newPassword.length < 6) {
          this.errors.newPassword = 'Password must be at least 6 characters';
          isValid = false;
        }

        if (this.formData.newPassword !== this.formData.confirmPassword) {
          this.errors.confirmPassword = 'Passwords do not match';
          isValid = false;
        }

        if (!this.formData.currentPassword) {
          this.errors.currentPassword = 'Current password is required to change password';
          isValid = false;
        }
      }

      return isValid;
    },
    async handleSubmit() {
      this.error = '';
      this.successMessage = '';
      this.errors = {};

      if (!this.validateForm()) {
        return;
      }

      this.loading = true;

      try {
        // Prepare update data
        const updateData = {
          firstName: this.formData.firstName,
          lastName: this.formData.lastName,
          email: this.formData.email,
          phone: this.formData.phone || null
        };

        // Only include password fields if new password is provided
        if (this.formData.newPassword) {
          updateData.currentPassword = this.formData.currentPassword;
          updateData.password = this.formData.newPassword;
        }

        // Get manager ID from managerUser
        const managerId = this.managerUser.id;
        const response = await axios.patch(
          `${process.env.VUE_APP_API_URL}/client-manager/profile/${managerId}`,
          updateData
        );

        // Update local storage with new manager data
        if (response.data.manager) {
          localStorage.setItem('managerUser', JSON.stringify(response.data.manager));
          this.$emit('updated', response.data.manager);
        }

        this.successMessage = response.data.message || 'Profile updated successfully';
        
        // Clear password fields
        this.formData.currentPassword = '';
        this.formData.newPassword = '';
        this.formData.confirmPassword = '';

        // Clear success message after 5 seconds
        setTimeout(() => {
          this.successMessage = '';
        }, 5000);

      } catch (error) {
        console.error('Error updating profile:', error);
        
        if (error.response?.data?.errors) {
          this.errors = error.response.data.errors;
        } else {
          this.error = error.response?.data?.message || 'Failed to update profile. Please try again.';
        }
      } finally {
        this.loading = false;
      }
    },
    resetForm() {
      this.loadUserData();
      this.errors = {};
      this.error = '';
      this.successMessage = '';
    }
  }
}
</script>
